import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { ApiService, CollabService, ReqViewService } from "../../../../../../../api";
import styles from "./StatusModal.module.css";
import { Flags, isFeatureEnabled, translate } from "../../../../../../../common/providers";
import { isNonEmptyArray } from "../../../../../../../common/utils";
import { CustomIconButton, CustomTooltip, icon, IconComponent } from "../../../../../../../common/components";

const icons = {
	no: { icon: "faTimesCircle", color: "var(--status-no)" },
	yes: { icon: "faCheckCircle", color: "var(--status-yes)" },
	pending: { icon: "faClock", color: "var(--status-pending)" },
	not_me: { icon: "aitNotEqualsCircle", color: "var(--status-not-me-20)" },
};

const StatusModal = ({
	data,
	getPartnership,
	informationId,
	onCommentIssueUpdate,
	open,
	partnership,
	onDispatchIssueComment,
	onOpenSidePanel,
}) => {
	const [otherStatus, setOtherStatus] = useState([]);
	const [focusedIssue, setFocusedIssue] = useState("");
	const [focusedPartnerIssueComment, setFocusedPartnerIssueComment] = useState("");
	const cancelTokenSourceRef = useRef(null);
	const companyId = useSelector(({ context }) => context.company.id);
	const fetchStatusList = useCallback(() => {
		ReqViewService.listStatusOnReq(
			{
				reqId: informationId,
			},
			cancelTokenSourceRef.current.token
		)
			.then((newData) => setOtherStatus(newData))
			.catch((err) => console.error(err));
	}, [informationId]);
	const handleSubmit = useCallback(() => {
		if (focusedIssue !== focusedPartnerIssueComment) {
			if (focusedIssue.length > 0) {
				CollabService.updateIssueComment({ informationId }, focusedIssue, cancelTokenSourceRef.current.token)
					.then(() => onDispatchIssueComment(focusedIssue))
					.catch((err) => {
						console.error(err);
					});
			} else if (focusedPartnerIssueComment) {
				CollabService.deleteIssueComment({ informationId }, cancelTokenSourceRef.current.token)
					.then(() => onDispatchIssueComment(focusedIssue))
					.catch((err) => {
						console.error(err);
					});
			}
			setFocusedPartnerIssueComment(focusedIssue);
		}
	}, [onDispatchIssueComment, focusedIssue, focusedPartnerIssueComment, informationId]);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	useEffect(() => {
		onCommentIssueUpdate(companyId, data.lastCommentIssue);
	}, [data.lastCommentIssue, data.informationId, companyId, onCommentIssueUpdate]);

	useEffect(() => {
		if (!data.validatedStatus || open) {
			if (isFeatureEnabled(Flags.PARTNERS)) {
				getPartnership();
			}
			fetchStatusList();
		}
	}, [data, open, getPartnership, fetchStatusList]);
	useEffect(() => {
		if (!open) {
			handleSubmit();
			setFocusedIssue("");
			setFocusedPartnerIssueComment("");
		}
	}, [open, handleSubmit]);
	const otherPositions =
		(isNonEmptyArray(otherStatus) && otherStatus.filter((position) => position.status !== "UNREVIEWED").length) ||
		0;
	const partnerPositions =
		(isNonEmptyArray(partnership) && partnership.filter((position) => position.status !== "UNREVIEWED").length) ||
		0;
	const hasPositions = otherPositions > 0 || partnerPositions > 0;
	const validations = useMemo(
		() => partnership.filter((position) => position.status && position.status !== "UNREVIEWED"),
		[partnership]
	);
	const StatusRow = ({ status, name }) => (
		<div className={styles.reviewerPosition}>
			<CustomTooltip showWhenOverflow title={name}>
				<div data-testid={`user.position.${status}`}>{name}</div>
			</CustomTooltip>
			<IconComponent
				color={icons[status.toLowerCase()].color}
				icon={icon[icons[status.toLowerCase()].icon]}
				size="md"
			/>
		</div>
	);
	return (
		<>
			{open && (
				<div className={styles.statusModal}>
					<div className={styles.statusHeader}>
						<div>
							<IconComponent color="var(--color-red-70)" icon={icon.aitStar} size="sm" />
							REQ_{data.informationId}
						</div>
						<CustomIconButton
							icon={icon.faChevronRight}
							size="xs"
							variant="outlined"
							onClick={onOpenSidePanel}
						/>
					</div>
					<>
						{(!hasPositions && (
							<span className={styles.emptyDescription}>
								{translate("smart-review.requirement.positions.dropdown.empty-state")}
							</span>
						)) || (
							<>
								<div className={styles.section}>
									<div className={styles.reviewerCounter}>
										<IconComponent color="var(--icon-secondary)" icon={icon.faCheck} />
										<span>
											{translate("smart-review.requirement.positions.dropdown.other-reviewers", {
												counter: otherPositions,
												total: otherStatus.length,
											})}
										</span>
									</div>
									<div>
										{otherStatus
											.filter((position) => position.status && position.status !== "UNREVIEWED")
											.map((position) => (
												<StatusRow name={position.displayName} status={position.status} />
											))}
									</div>
								</div>
								<div className={styles.section}>
									<div className={styles.reviewerCounter}>
										<IconComponent color="var(--icon-secondary)" icon={icon.faCheck} />
										<span>
											{translate("smart-review.requirement.positions.dropdown.other-validators", {
												counter: partnerPositions,
												total: partnership.length,
											})}
										</span>
									</div>
									<div>
										{(validations.length > 0 &&
											validations.map((position) => (
												<StatusRow name={position.companyName} status={position.status} />
											))) || (
											<span className={styles.validationNone}>
												{translate(
													"smart-review.requirement.positions.dropdown.other-validators.empty"
												)}
											</span>
										)}
									</div>
								</div>
							</>
						)}
					</>
				</div>
			)}
		</>
	);
};

export default StatusModal;
